import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/apiClient";
import { Logo, LegalAgreeCheckbox, PublicFooter } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Login() {
  const { requestOtp, verifyOtp } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/app";
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [devCode, setDevCode] = useState("");
  const [deliveryPending, setDeliveryPending] = useState(false);
  const [agreeLegal, setAgreeLegal] = useState(false);
  const [loading, setLoading] = useState(false);

  const send = async ({ resend = false } = {}) => {
    setLoading(true);
    try {
      const data = await requestOtp(phone);
      setDevCode(data.dev_code || "");
      setDeliveryPending(data.delivery === "pending");
      setStep("code");
      if (data.dev_code) {
        toast.info(`Dev OTP (mock): ${data.dev_code}`);
      } else if (data.delivery === "pending") {
        toast.message("Sending your code… it should arrive shortly.");
      } else if (resend) {
        toast.success("A new code was sent.");
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    if (!agreeLegal) {
      toast.error("Please agree to the Terms and Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      await verifyOtp(phone, code, name, { acceptLegal: true });
      nav(redirect);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-md rounded-[16px] border border-border bg-card p-8 shadow-[0_20px_40px_rgba(27,122,94,0.1)]">
          <Logo className="mb-6" />
          <h1 className="text-2xl font-black tracking-tight">Sign in to refer & earn</h1>
          <p className="mt-1 text-sm text-muted-foreground">Phone verification — quick and passwordless.</p>

          {step === "phone" ? (
            <div className="mt-6 space-y-3">
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Phone number</label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 000 0002" data-testid="login-phone-input" />
              <Button className="w-full bg-brand-green hover:bg-brand-green-dark" disabled={loading || phone.length < 7} onClick={() => send()} data-testid="login-send-otp-btn">
                {loading ? "Sending…" : "Send code"}
              </Button>
              <p className="pt-2 text-center text-xs text-muted-foreground">
                By continuing you can review our{" "}
                <a href="/terms" className="underline" data-testid="login-terms-preview-link">Terms</a>
                {" "}and{" "}
                <a href="/privacy" className="underline" data-testid="login-privacy-preview-link">Privacy Policy</a>.
              </p>
            </div>
          ) : (
            <div className="mt-6 space-y-3">
              {devCode && (
                <div className="rounded-[10px] bg-brand-gold/15 px-3 py-2 text-sm text-brand-gold-dark" data-testid="dev-otp-hint">
                  Mock OTP: <span className="font-mono font-bold">{devCode}</span> (SMS is simulated)
                </div>
              )}
              {deliveryPending && !devCode ? (
                <div className="rounded-[10px] bg-brand-green/10 px-3 py-2 text-sm text-brand-green-dark" data-testid="otp-delivery-pending">
                  Sending your code… if it doesn&apos;t arrive in a minute, tap Resend.
                </div>
              ) : null}
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Verification code</label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" data-testid="login-code-input" />
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Your name (optional)</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Sam" data-testid="login-name-input" />
              <LegalAgreeCheckbox checked={agreeLegal} onCheckedChange={setAgreeLegal} id="login-legal-agree" />
              <Button
                className="w-full bg-brand-green hover:bg-brand-green-dark"
                disabled={loading || code.length < 4 || !agreeLegal}
                onClick={verify}
                data-testid="login-verify-btn"
              >
                {loading ? "Verifying…" : "Verify & continue"}
              </Button>
              <Button
                variant="outline"
                className="w-full"
                disabled={loading}
                onClick={() => send({ resend: true })}
                data-testid="login-resend-otp-btn"
              >
                {loading ? "Sending…" : "Resend code"}
              </Button>
              <button className="w-full text-sm text-muted-foreground underline" onClick={() => setStep("phone")} data-testid="login-back-btn">
                Use a different number
              </button>
            </div>
          )}
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
